import { Button, Spinner } from '@monksflow/monks-ui';
import { RefreshCwIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { VideoPlayer } from '@/shared/ui/VideoPlayer';
import { useGetDownloadUrl } from '@/shared/hooks/useGetDownloadUrl';
import type { VideoCardProps } from '../tabs/ProfileMediaTab/components/VideoSection/components/VideoCard/VideoCard.types';

const Placeholder = ({ children }: { children: React.ReactNode }) => (
  <div className="flex size-full items-center justify-center bg-muted">{children}</div>
);

export function VideoCardV2({
  label,
  videoAssetId,
  metadata,
  hasError,
  errorMessage,
  isRetrying,
  onRetry,
}: VideoCardProps) {
  const { t } = useTranslation('audiences');
  const { downloadUrl, isLoading } = useGetDownloadUrl(videoAssetId ?? undefined);

  return (
    <div className="flex flex-col gap-xs">
      <span className="text-xs text-foreground">{label}</span>

      <div className="aspect-video overflow-hidden rounded-md border border-border">
        {isRetrying || isLoading ? (
          <Placeholder>
            <Spinner />
          </Placeholder>
        ) : hasError ? (
          <div className="flex size-full flex-col overflow-y-auto bg-muted">
            {/* Centred by auto margins rather than justify-content: a reason long
                enough to overflow this 16:9 box would sit partly above the scroll
                origin and be unreachable, whereas auto margins collapse to zero
                once the content stops fitting. */}
            <div className="m-auto flex flex-col items-center gap-xs p-sm">
              <p className="text-center text-xs text-muted-foreground">
                {errorMessage ?? t('audiences.videoCard.couldnTGenerateThisVideo')}
              </p>
              {onRetry && (
                <Button variant="outline" size="sm" onClick={onRetry}>
                  <RefreshCwIcon className="size-4" />
                  {t('audiences.videoCard.tryAgain')}
                </Button>
              )}
            </div>
          </div>
        ) : downloadUrl ? (
          <VideoPlayer videoSrc={downloadUrl} className="scale-[1.02]" />
        ) : (
          <Placeholder>
            <span className="text-xs text-muted-foreground">
              {t('audiences.videoCard.noVideo')}
            </span>
          </Placeholder>
        )}
      </div>

      {metadata && (
        <div className="flex flex-col gap-3xs">
          {metadata.createdAt && (
            <span className="text-xs text-muted-foreground">
              {t('audiences.videoCard.created', { createdAt: metadata.createdAt })}
            </span>
          )}
          {/* Only the detail video is prompted; the thumbnail is derived from it. */}
          {metadata.promptUsed && metadata.videoType === 'detail' && (
            <div className="flex flex-col gap-3xs">
              <span className="text-xs text-muted-foreground">
                {t('audiences.videoCard.promptUsed')}
              </span>
              <span className="text-xs text-foreground">{metadata.promptUsed}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

VideoCardV2.displayName = 'VideoCardV2';
